import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import ModalTW from '@medflex/components/stories/Modal';
import TextComposerModalBody from '@medflex/components/stories/TextComposerModalBody';
import TextSnippetsPopup from '@medflex/components/stories/TextSnippetsPopup';
import useModal from '../hooks/useModal';
import useIsModal from '../hooks/useIsModal';
import usePhysicianStatusEdit from '../hooks/usePhysicianStatusEdit';
import useSnippets from '../hooks/useSnippets';

const Actions = props => {
  const { onInsert } = props;
  const { snippets } = useSnippets();

  const handleInsertSnippet = ({ id }) => {
    const snippet = snippets.find(sn => sn.id === id);
    if (snippet.text) {
      onInsert(snippet.text);
    }
  };

  return [
    <div key="snippets-popup" className="mt-4 text-slate-500">
      <TextSnippetsPopup snippets={snippets} onClick={handleInsertSnippet} />
    </div>,
  ];
};

Actions.propTypes = {
  onInsert: PropTypes.func,
};

const StatusComposeModal = () => {
  const { status, isSaving, savePhysicianStatus } = usePhysicianStatusEdit();
  const { t } = useTranslation('physician');
  const title = status ? t('modals.status.title') : t('modals.status.title_new');
  const inputElementRef = useRef();
  const { isModal } = useIsModal();
  const isWideScreen = useIsWideScreen();
  const { close } = useModal();
  const [text, setText] = useState(status);

  useEffect(() => {
    if (inputElementRef.current && isModal && isWideScreen) {
      inputElementRef.current.focus();
    }
  }, [isModal, isWideScreen]);

  const handleSubmit = async (event, { value }) => {
    const success = await savePhysicianStatus({ status: value.trim() });
    if (success) {
      showSuccessToast(t('toasts.status.success.title'), t('toasts.status.success.text'), {
        toastId: 'statusEditSuccess',
      });
      trackEvent(event, {
        zone: 'Arzt_Hinweis',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.FORM,
        actionText: 'save',
      });
    } else {
      showAlertToast(t('toasts.status.alert.title'), t('toasts.status.alert.text'), {
        toastId: 'statusEditFailed',
      });
      trackEvent(event, {
        zone: 'Arzt_Hinweis',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.FORM,
        actionText: 'failure',
      });
    }
    close({ modal: 'status' });
  };

  return (
    <ModalTW title={title} showCloseButton onClickCloseButton={() => close({ modal: 'status' })}>
      <TextComposerModalBody
        value={text}
        maxRows={isWideScreen ? 10 : undefined}
        infoText={t('modals.status.info', { allowHtml: true })}
        saveButtonLabel={t('modals.status.submit')}
        state={isSaving ? 'loading' : 'composing'}
        isTextTop
        // eslint-disable-next-line react/no-unstable-nested-components
        actions={({ textareaValue }) => (
          <Actions onInsert={value => setText(`${textareaValue}${value}`)} />
        )}
        setRef={inputElement => {
          inputElementRef.current = inputElement;
        }}
        disableSubmit={false}
        onSubmit={(event, value) => handleSubmit(event, { value })}
      />
      <p className="mt-4 text-sm text-slate-500">{t('modals.status.hint')}</p>
    </ModalTW>
  );
};

export default StatusComposeModal;
